import React, { useEffect, useMemo, useState } from 'react';

import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    Select,
    TextField
} from '@material-ui/core';
import { Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { KeyboardDatePicker } from '@material-ui/pickers';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { fetchCities } from '../../../store/slices/forms';
import { setLicenseVehicleDocuments } from '../../../store/slices/vehicle';
import vehiclesEndpoint from '../../../api/services/vehiclesEndpoint';
import { STATUS_CODES } from '../../../config/status';
import NumberFormatCustom from '../../NumberFormatCustom';
import moment from 'moment';
import _ from 'lodash';

function AdditionalDataSurvey(props) {
    const dispatch = useDispatch();
    const { classes, values, errors, setFieldValue, contextProps: { canEdit } } = props;

    const request = useSelector((state) => state.requests.details);
    const cities = useSelector(state => state.forms.cities);
    const [surveyDocuments, setSurveyDocuments] = useState([]);

    useEffect(() => {
        vehiclesEndpoint.getVehiclesDocumentsSurvey().then(response => {
            setSurveyDocuments(response?.data || []);
        });
    }, []);

    useEffect(() => {
        if (request.cdSituacao === undefined && values.boVeiculoNovo === undefined) {
            setFieldValue('boVeiculoNovo', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request.cdSituacao, values.boVeiculoNovo]);

    useEffect(() => {
        const currentDocuments = surveyDocuments
            .filter(doc => {
                if (doc.boSempreMostrar === true) return true;

                if (values.deUtilizacao === 'Sistema') {
                    if (doc.boMostrarUtilizacaoSistema != null) return true;
                    return doc.boMostrarVeiculoNovo === !!values.boVeiculoNovo;
                }
                if (doc.boMostrarUtilizacaoSistema != null) return false;
                return doc.boMostrarVeiculoNovo === !!values.boVeiculoNovo;
            });
        dispatch(setLicenseVehicleDocuments(currentDocuments || []));
        setFieldValue('nuDocumentosVistoria', currentDocuments.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyDocuments, values.deUtilizacao, values.boVeiculoNovo]);

    useEffect(() => {
        const init = async () => {
            if (cities.length === 0) {
                await dispatch(fetchCities());
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cities]);

    const validateDate = (value) => {
        if (!value) return 'Campo obrigatório';
        if (!moment(value).isValid()) return 'Data inválida';
        if (moment(value).isAfter(new Date())) return 'A data deve ser menor que ou igual à data atual';
        return '';
    }

    const disableField = useMemo(() => {
        return ![undefined, STATUS_CODES.EM_CONSTRUCAO, STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS].includes(request.cdSituacao);
    }, [request.cdSituacao]);

    return (<>
        <Grid item xs={12}>
            <h3 className={classes.title}>Situação do Veículo</h3>
        </Grid>

        <Grid item xs={12}>
            <FormControlLabel
                label="Veículo novo?"
                name="boVeiculoNovo"
                onClick={e => setFieldValue(e.target.name, e.target.checked)}
                control={<Checkbox color="primary" checked={!!values.boVeiculoNovo}/>}
                labelPlacement="right"
                disabled={!canEdit || disableField}
            />
        </Grid>

        {!values.boVeiculoNovo && (<>
            <Grid item xs={12}>
                <h3 className={classes.title}>Informações da Vistoria</h3>
            </Grid>

            <Grid item xs={12} sm={8} md={4}>
                <Field
                    label={'Identificador da Vistoria*'}
                    name="idVistoria"
                    variant="filled"
                    margin="dense"
                    value={values.idVistoria || ''}
                    onChange={e => setFieldValue('idVistoria', e.target.value)}
                    as={TextField}
                    fullWidth
                    error={!!errors?.idVistoria}
                    helperText={errors?.idVistoria || ''}
                    disabled={!canEdit}
                    inputProps={{ maxLength: 20 }}
                />
            </Grid>

            <Grid item xs={12} sm={4} md={4}>
                <Field
                    label="Data da vistoria*"
                    name="dtVistoria"
                    disableToolbar
                    format="DD/MM/YYYY"
                    margin="dense"
                    value={values.dtVistoria || null}
                    onChange={v => setFieldValue('dtVistoria', v)}
                    error={!!errors?.dtVistoria}
                    helperText={errors?.dtVistoria || ''}
                    as={KeyboardDatePicker}
                    fullWidth
                    validate={validateDate}
                    disabled={!canEdit}
                />
            </Grid>

            <Grid item xs={12} sm={4} md={4}>
                <FormControl variant="filled" margin="dense" fullWidth error={!!errors?.tpSituacao}>
                    <InputLabel htmlFor="tpSituacao" shrink={true}>
                        Resultado vistoria*
                    </InputLabel>
                    <Select
                        name="tpSituacao"
                        value={values.tpSituacao}
                        onChange={e => setFieldValue(e.target.name, e.target.value)}
                        error={!!errors?.tpSituacao}
                        helperText={errors?.tpSituacao || ''}
                        native
                        disabled={!canEdit}
                    >
                        <option value=""></option>
                        <option value="A">APROVADO</option>
                        <option value="R">REPROVADO</option>
                    </Select>
                    {!!errors?.tpSituacao ? (
                        <FormHelperText error>{errors.tpSituacao}</FormHelperText>
                    ) : null}
                </FormControl>
            </Grid>

            <Grid item xs={12} sm={8} md={4}>
                <Autocomplete
                    label={'Município de preferência vistoria*'}
                    name="cdMunicipioVistoria"
                    options={cities}
                    value={values?.municipioVistoria || null}
                    onChange={(e, value) => {
                        if (!value) return;
                        const found = _.find(cities, ['cdMunicipio', Number(value.cdMunicipio)]);
                        if (found) {
                            setFieldValue('cdMunicipioVistoria', value.cdMunicipio);
                            setFieldValue('municipioVistoria', found);
                        }
                    }}
                    getOptionLabel={(option) => !option ? '' : option.nmMunicipio + ' - ' + option.uf?.sgUf}
                    getOptionSelected={(option, value) => (!option || !value) ? false : option.cdMunicipio === value.cdMunicipio}
                    renderInput={(params) => (<TextField
                        {...params}
                        label={'Município de preferência vistoria*'}
                        fullWidth
                        variant="filled"
                        margin="dense"
                        error={!!errors.cdMunicipioVistoria}
                        helperText={errors?.cdMunicipioVistoria || ''}
                    />)}
                    error={!!errors?.cdMunicipioVistoria}
                    helperText={errors?.cdMunicipioVistoria || ''}
                    fullWidth
                    disabled={!canEdit}
                />
            </Grid>
        </>)}

        {values.boVeiculoNovo && (<>
            <Grid item xs={12}>
                <h3 className={classes.title}>Informações da Nota Fiscal</h3>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <Field
                    label="Data da nota fiscal*"
                    name="dtNotaFiscal"
                    disableToolbar
                    format="DD/MM/YYYY"
                    margin="dense"
                    value={values.dtNotaFiscal || null}
                    onChange={v => setFieldValue('dtNotaFiscal', v)}
                    error={!!errors.dtNotaFiscal}
                    helperText={errors?.dtNotaFiscal || ''}
                    as={KeyboardDatePicker}
                    fullWidth
                    validate={validateDate}
                    disabled={disableField}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    label={'Valor do bem (R$)*'}
                    name="nuValorBem"
                    value={values?.nuValorBem}
                    margin="dense"
                    variant="filled"
                    onChange={e => setFieldValue(e.target.name, e.target.value)}
                    error={!!errors?.nuValorBem}
                    helperText={errors?.nuValorBem || ''}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        inputProps: {
                            style: { textAlign: 'right' },
                            isAllowed: ({ floatValue = 0 }) => floatValue < 1000000000}
                    }}
                    fullWidth
                    disabled={disableField}
                />
            </Grid>
        </>)}
    </>);
}

export default AdditionalDataSurvey;
